<script setup>
//CampaignConfigureList.vue - Nova Versao CORRIGIDA - COMPLETA
import { nextTick, onMounted, ref, watch, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { onBeforeRouteLeave, useRouter } from 'vue-router';
import { useTableFilterContext } from './tableFilterProvider.js';
import TabContentWrapper from './TabContentWrapper.vue';

// Importar hooks customizados
import {
  useTabManagement,
  useDataFetching,
  useFormValidation,
  useComputedProperties,
} from './hooks/index.js';
import { useEventHandlers } from './hooks/useEventHandlers.js';

// Importar componentes
import PageHeader from 'dashboard/routes/dashboard/settings/SettingsSubPageHeader.vue';
import SelectedPublic from './SelectedPublic.vue';
import PlaceholdersDynamic from './PlaceholdersDynamic.vue';
import TemplatePreview from './TemplatePreview.vue';
import TemplatePreviewPhone from '../../../whatsapp_app/templates/components/TemplatePreviewPhone.vue';
import Filters from './SelectedFilters.vue';
import TablePreviewModal from './TablePreviewModal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'dashboard/components-shadcn/new-york/ui/tabs';

// Inicializar dependências
const store = useStore();
const { t } = useI18n();
const router = useRouter();

// Context detection: whatsapp_app vs whatsapp_oficial
const isAppContext = router.currentRoute.value.path.includes('/new-app/');
const homeRoute = isAppContext ? 'campaigns_whatsapp_app_home' : 'campaigns_whatsapp_official_home';

// Baileys template compatibility: wraps local template to PlaceholdersDynamic-compatible format
const localTemplate = computed(() => {
  const data = store.getters['campaigns/getCampaignData'];
  return data?.template || null;
});

const selectedTemplateCompat = computed(() => {
  if (!isAppContext) return null;
  const tpl = localTemplate.value;
  if (!tpl) return { components: [] };
  return {
    ...tpl,
    components: [{ type: 'BODY', text: tpl.content || '' }],
  };
});

// Header content for app context (no WhatsApp API quality rating)
const appHeaderTitle = 'Configurar Público da Campanha';
const appHeaderContent = computed(() => {
  return 'Selecione a tabela, mapeie os placeholders do template e aplique filtros para definir o público-alvo da campanha.';
});

const { filterTypes, setSelectedTable } = useTableFilterContext();

// Props
const props = defineProps({
  templateName: {
    type: String,
    default: 'Nova Campanha',
  },
});

// Inicializar hooks
const tabManagement = useTabManagement();
const dataFetching = useDataFetching();
const formValidation = useFormValidation();

// Estado adicional
const selectedTable = ref('');
const showModal = ref(false);
const isInitializing = ref(true);
const placeholderComponentKey = ref(0);
const filtersComponentKey = ref(0); // 🔧 NOVO: Key para forçar re-render do Filters
const isRestoringFilters = ref(false); // 🔧 NOVO: Flag para controlar restauração

// Estados para controle de loading
const campaignDataLoading = ref(false);
const showSkeletonContent = ref(true);

// Validação dos placeholders
const isPlaceholdersValid = ref(false);

// Initial data for components
const initialTableData = ref({
  selectedTable: '',
  selectedPhoneColumn: '',
  selectedContactColumn: '',
});

// Propriedades computadas
const computedProps = useComputedProperties(
  dataFetching.whatsappMessageLimit,
  dataFetching.tableData,
  dataFetching.totalCount,
  formValidation.filters,
  dataFetching.whatsappLimitError,
  dataFetching.isFetchingWhatsappLimit
);

// Computed para verificar se dados da campanha estão carregando
const isCampaignDataLoading = computed(() => {
  const campaignData = store.getters['campaigns/getCampaignData'];
  return campaignData?.isLoading || false;
});

// Watch para monitorar mudanças no estado de carregamento
watch(isCampaignDataLoading, (newValue) => {
  campaignDataLoading.value = newValue;
  if (!newValue) {
    // Pequeno delay para suavizar a transição
    setTimeout(() => {
      showSkeletonContent.value = false;
    }, 300);
  }
}, { immediate: true });

// Validação da tabela
const isTableValidLocal = computed(() => {
  // Só executar validação se estivermos na tab table ou se já foi validada antes
  if (tabManagement.activeTab.value !== 'table' && tabManagement.tabValidation.value.table) {
    return tabManagement.tabValidation.value.table;
  }
  
  const hasTable = Boolean(selectedTable.value);
  
  // Buscar dados do Vuex se ref não estiver disponível
  let hasPhoneColumn, hasContactColumn;
  
  if (formValidation.selectedPublicRef.value?.selectedPhoneColumn !== undefined) {
    hasPhoneColumn = Boolean(formValidation.selectedPublicRef.value.selectedPhoneColumn);
    hasContactColumn = Boolean(formValidation.selectedPublicRef.value.selectedContactColumn);
  } else {
    // Fallback para dados do Vuex
    const currentData = store.getters['campaigns/getCampaignData'];
    hasPhoneColumn = Boolean(currentData?.selectedPhoneColumn);
    hasContactColumn = Boolean(currentData?.selectedContactColumn);
    
  }
  
  const isValid = hasTable && hasPhoneColumn && hasContactColumn;
  
  return isValid;
});

// Watch que só atualiza quando realmente necessário
watch(isTableValidLocal, (newValue, oldValue) => {
  // Só atualizar se realmente mudou
  if (newValue !== oldValue) {
    tabManagement.updateValidation('table', newValue);
  }
}, { immediate: true });

// Watch nos campos individuais apenas quando na tab correta
watch([
  () => selectedTable.value,
  () => formValidation.selectedPublicRef.value?.selectedPhoneColumn,
  () => formValidation.selectedPublicRef.value?.selectedContactColumn,
  () => tabManagement.activeTab.value
], () => {
}, { immediate: true });

// Computed: verifica se o template realmente possui placeholders ({{...}})
const templateHasPlaceholders = computed(() => {
  const tpl = isAppContext ? selectedTemplateCompat.value : computedProps.selectedTemplate.value;
  const bodyComponent = tpl?.components?.find(c => c.type === 'BODY');
  const text = bodyComponent?.text || '';
  const matches = text.match(/\{\{[^{}]+\}\}/g);
  return matches && matches.length > 0;
});

// 🔧 CORREÇÃO 3: Validação de placeholders que usa os hooks corretamente
const validatePlaceholdersTab = () => {
  // Se o template não tem placeholders, a tab é automaticamente válida
  if (!templateHasPlaceholders.value) {
    tabManagement.updateValidation('placeholders', true);
    isPlaceholdersValid.value = true;
    return true;
  }

  const hasPlaceholders = Object.keys(formValidation.placeholderSelections.value || {}).length > 0;
  const allPlaceholdersFilled = Object.values(formValidation.placeholderSelections.value || {})
    .every(value => value && value.trim() !== '');
  const isValid = hasPlaceholders && allPlaceholdersFilled;

  // 🔧 CORREÇÃO: Usar updateValidation que agora marca como completada
  tabManagement.updateValidation('placeholders', isValid);
  isPlaceholdersValid.value = isValid;

  return isValid;
};

// Event handlers - 🔧 CORREÇÃO 4: Usar handlers que salvam no Vuex
const eventHandlers = useEventHandlers(
  tabManagement,
  dataFetching,
  formValidation,
  computedProps,
  selectedTable,
  showModal,
  setSelectedTable,
  props
);

// Wrapper para mudanças de filtros
const onFiltersChangeWrapper = async (newFilters) => {
  if (isRestoringFilters.value) {
    return;
  }
  
  await eventHandlers.onFiltersChange(newFilters);
};

// Handler de placeholders
const onPlaceholdersChanged = async (selections) => {
  
  if (selections && typeof selections === 'object') {
    await eventHandlers.onPlaceholdersChange(selections);
    await nextTick();
    validatePlaceholdersTab();
  }
};


// Route guard
onBeforeRouteLeave((to, from, next) => {
  const isLeavingFlow =
    to.name === 'campaigns_whatsapp_api_templates' ||
    to.name === homeRoute ||
    !to.name?.includes('campaigns');
  if (isLeavingFlow) {
    store.dispatch('campaigns/clearCampaignData');
  }
  next();
});

// Função de inicialização
const initDateStorage = async (campaignData) => {

    // 🔧 VERIFICAÇÃO DEFENSIVA: Garantir que os refs estão disponíveis
  if (!formValidation.selectedPublicRef.value) {
    console.error('❌ [initDateStorage] selectedPublicRef.value é null! Aguardando...');
    
    // Tentar aguardar o ref ficar disponível
    await waitForComponentRefs();
    
    // Se ainda não estiver disponível, pular inicialização da tabela
    if (!formValidation.selectedPublicRef.value) {
      console.error('❌ [initDateStorage] selectedPublicRef.value ainda é null após aguardar!');
      // Continue com outras inicializações que não dependem deste ref
      return;
    }
  }
  
  const {
    selectedTable: campaignTable,
    selectedPhoneColumn,
    placeholders,
    filters: campaignFilters,
    selectedContactColumn
  } = campaignData;

  // Inicializar tabela
  if (campaignTable) {
    if (!formValidation.selectedPublicRef.value) {
      console.error('❌ [initDateStorage] selectedPublicRef.value é null!');
      return;
    } 

    formValidation.selectedPublicRef.value.selectedTable = campaignTable;
    selectedTable.value = campaignTable;
    
    if (dataFetching.columns.value.length === 0) {
      await Promise.all([
        dataFetching.fetchColumns(campaignTable),
        setSelectedTable(campaignTable),
      ]);
    }
  }

  // Inicializar colunas
  if (selectedPhoneColumn && formValidation.selectedPublicRef.value) {
    formValidation.selectedPublicRef.value.selectedPhoneColumn = selectedPhoneColumn;
  }

  if (selectedContactColumn && formValidation.selectedPublicRef.value) {
    formValidation.selectedPublicRef.value.selectedContactColumn = selectedContactColumn;
  }

  // Inicializar placeholders
  if (placeholders && Object.keys(placeholders).length > 0) {
    const validPlaceholders = {};
    Object.keys(placeholders).forEach((key) => {
      const columnExists = dataFetching.columns.value.some(col => col.column_name === placeholders[key]) ||
                          dataFetching.allColumns.value.some(col => col.column_name === placeholders[key]);
      if (columnExists) {
        validPlaceholders[key] = placeholders[key];
      }
    });
    
    // 🔧 CORREÇÃO: Definir placeholders no hook primeiro
    formValidation.placeholderSelections.value = { ...validPlaceholders };
    
    // 🔧 CORREÇÃO: Aguardar o próximo tick antes de definir no componente
    await nextTick();
    
    if (formValidation.placeholdersDynamicRef.value) {
      // 🔧 IMPORTANTE: Forçar a atualização do componente
      formValidation.placeholdersDynamicRef.value.placeholderSelections = { ...validPlaceholders };
      
      // 🔧 NOVO: Se o componente tem método para inicializar, chamá-lo
      if (formValidation.placeholdersDynamicRef.value.initializeSelections) {
        formValidation.placeholdersDynamicRef.value.initializeSelections(validPlaceholders);
      }
      
      // 🔧 NOVO: Se o componente tem método para atualizar, chamá-lo
      if (formValidation.placeholdersDynamicRef.value.updateSelections) {
        formValidation.placeholdersDynamicRef.value.updateSelections(validPlaceholders);
      }
    }
  }

  // 🔧 CORREÇÃO PRINCIPAL: Inicialização específica para filtros
  if (campaignFilters && Array.isArray(campaignFilters) && campaignFilters.length > 0) {
    
    // Primeiro, atualizar o estado local do hook
    formValidation.filters.value = [...campaignFilters];
    
    // Aguardar filterTypes estar disponível
    if (!filterTypes.value || !Array.isArray(filterTypes.value) || filterTypes.value.length === 0) {
      await new Promise(resolve => {
        const unwatch = watch(
          () => filterTypes.value,
          (newFilterTypes) => {
            if (newFilterTypes && Array.isArray(newFilterTypes) && newFilterTypes.length > 0) {
              unwatch();
              resolve();
            }
          },
          { immediate: true }
        );
      });
    }
    
    // Validar filtros contra filterTypes disponíveis
    const validFilters = campaignFilters.filter(filter => {
      const typeExists = filterTypes.value.some(type => type.attributeKey === filter.attributeKey);
      if (!typeExists) {
        console.warn('⚠️ [initDateStorage] Filtro inválido encontrado:', filter.attributeKey);
      }
      return typeExists;
    });
    
    // Forçar re-render do componente antes de definir filtros
    filtersComponentKey.value++;
    await nextTick();
    
    // Aguardar o componente estar disponível
    let attempts = 0;
    const maxAttempts = 10;
    
    while (!formValidation.filtersRef.value && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
    
    if (formValidation.filtersRef.value) {
      
      // Tentar múltiplas abordagens para definir os filtros
      if (formValidation.filtersRef.value.setFilters) {
        formValidation.filtersRef.value.setFilters(validFilters);
      } else if (formValidation.filtersRef.value.initializeFilters) {
        formValidation.filtersRef.value.initializeFilters(validFilters);
      } else {
        formValidation.filtersRef.value.filters = [...validFilters];
      }
      
      // Forçar atualização se o componente tiver método para isso
      if (formValidation.filtersRef.value.forceUpdate) {
        formValidation.filtersRef.value.forceUpdate();
      }
      

    } else {
      console.error('❌ [initDateStorage] Componente Filters não disponível após tentativas');
    }
  }

  // Inicializar dados iniciais
  initialTableData.value = {
    selectedTable: campaignData.selectedTable || '',
    selectedPhoneColumn: campaignData.selectedPhoneColumn || '',
    selectedContactColumn: campaignData.selectedContactColumn || '',
  };

  if (placeholders && Object.keys(placeholders).length > 0) {
    // 🔧 CORREÇÃO: Forçar re-render do componente antes de inicializar
    placeholderComponentKey.value++;
    await nextTick();
  }

  // Validar após inicialização usando o sistema centralizado
  await nextTick();
  
  // A validação da tabela será feita automaticamente pelo watch do isTableValidLocal
  // Validar placeholders: se há dados salvos OU se o template não tem placeholders
  if ((placeholders && Object.keys(placeholders).length > 0) || !templateHasPlaceholders.value) {
    validatePlaceholdersTab();
  }
};

// Função auxiliar para aguardar dados completos da campanha
const waitForCampaignDataComplete = (timeout = 10000) => {
  return new Promise((resolve) => {
    const startTime = Date.now();
    
    const checkData = () => {
      const data = store.getters['campaigns/getCampaignData'];
      
      if (data && !data.isLoading && data.messageLimit) {
        resolve(true);
        return;
      }
      
      if (Date.now() - startTime > timeout) {
        resolve(false);
        return;
      }
      
      setTimeout(checkData, 100);
    };
    
    checkData();
  });
};

// Função auxiliar para aguardar filterTypes
const waitForFilterTypes = () => {
  return new Promise(resolve => {
    const unwatch = watch(
      () => filterTypes.value,
      (newFilterTypes) => {
        if (newFilterTypes && Array.isArray(newFilterTypes)) {
          unwatch();
          resolve();
        }
      },
      { immediate: true }
    );
    
    setTimeout(() => {
      unwatch();
      resolve();
    }, 5000);
  });
};


// onMounted refatorado - CORREÇÃO para o problema do skeleton
onMounted(async () => {
  try {
    // 1. Verificar estado inicial dos dados da campanha
    const campaignData = store.getters['campaigns/getCampaignData'];
    
    if (!campaignData || !campaignData.template) {
      router.push({ name: homeRoute });
      return;
    }
    
    // 2. Se dados estão carregando, aguardar conclusão
    if (campaignData.isLoading) {
      isInitializing.value = true;
      
      const dataReady = await waitForCampaignDataComplete(10000);
      
      if (!dataReady) {
        router.push({ name: homeRoute });
        return;
      }
    }
    
    // 3. Carregar dados básicos da aplicação em paralelo
    const fetchPromises = [dataFetching.fetchTables()];
    if (!isAppContext) {
      fetchPromises.push(dataFetching.fetchWhatsappLimit());
    }
    await Promise.all(fetchPromises);
    
    // 4. Aguardar filterTypes se necessário
    if (!filterTypes.value || !Array.isArray(filterTypes.value)) {
      await waitForFilterTypes();
    }

    // 5. Obter dados atualizados após carregamento completo
    const savedData = computedProps.selectedCampaignData.value;
    
    if (savedData?.selectedTable) {
      await dataFetching.fetchTableColumns(savedData.selectedTable);
      await setSelectedTable(savedData.selectedTable);
    }

    // 6. CORREÇÃO PRINCIPAL: Ocultar skeleton ANTES de inicializar componentes
    isInitializing.value = false;
    showSkeletonContent.value = false;
    
    // 7. Aguardar o próximo tick para garantir que os componentes foram montados
    await nextTick();
    
    // 8. Aguardar os refs dos componentes estarem disponíveis
    await waitForComponentRefs();
    
    // 9. Agora sim inicializar com dados completos
    formValidation.initializeFromVuex();
    await initDateStorage(savedData);
    
  } catch (error) {
    console.error('Erro na inicialização:', error);
    isInitializing.value = false;
    showSkeletonContent.value = false;
    router.push({ name: homeRoute });
  }
});

const waitForComponentRefs = async (timeout = 5000) => {
  const startTime = Date.now();
  
  return new Promise((resolve) => {
    const checkRefs = () => {
      // Verificar se pelo menos o ref principal está disponível
      if (formValidation.selectedPublicRef.value) {
        resolve(true);
        return;
      }
      
      if (Date.now() - startTime > timeout) {
        console.warn('⚠️ [waitForComponentRefs] Timeout aguardando refs dos componentes');
        resolve(false);
        return;
      }
      
      // Tentar novamente após um pequeno delay
      setTimeout(checkRefs, 100);
    };
    
    checkRefs();
  });
};

// 🔧 CORREÇÃO: Watch melhorado para mudanças de tab
watch(() => tabManagement.activeTab.value, async (newTab, oldTab) => {
  
  if (newTab === 'placeholders') {
    await nextTick();

    // Se template não tem placeholders, auto-validar imediatamente
    if (!templateHasPlaceholders.value) {
      tabManagement.updateValidation('placeholders', true);
      isPlaceholdersValid.value = true;
    } else {
      const currentData = store.getters['campaigns/getCampaignData'];

      if (currentData && currentData.placeholders && Object.keys(currentData.placeholders).length > 0) {
        // Atualizar estado local primeiro
        formValidation.placeholderSelections.value = { ...currentData.placeholders };

        // Aguardar e então atualizar o componente
        await nextTick();

        if (formValidation.placeholdersDynamicRef.value) {
          // Forçar atualização do componente
          formValidation.placeholdersDynamicRef.value.placeholderSelections = { ...currentData.placeholders };

          // Se o componente tem método para forçar atualização
          if (formValidation.placeholdersDynamicRef.value.forceUpdate) {
            formValidation.placeholdersDynamicRef.value.forceUpdate();
          }

          // Incrementar key para forçar re-render se necessário
          placeholderComponentKey.value++;
        }

        // Validar após restauração
        await nextTick();
        validatePlaceholdersTab();
      }
    }
  }
  
  // 🔧 NOVO: Lógica específica para tab filters
  if (newTab === 'filters') {
    
    await nextTick();
    const currentData = store.getters['campaigns/getCampaignData'];
    
    if (currentData && currentData.filters && Array.isArray(currentData.filters) && currentData.filters.length > 0) {
      
      // 🔧 IMPORTANTE: Marcar que estamos restaurando
      isRestoringFilters.value = true;
      
      // Atualizar estado local primeiro
      formValidation.filters.value = [...currentData.filters];
      
      // Forçar re-render do componente
      filtersComponentKey.value++;
      
      // Aguardar e então atualizar o componente
      await nextTick();
      
      // Aguardar um pouco mais para garantir montagem
      await new Promise(resolve => setTimeout(resolve, 100));
      
      if (formValidation.filtersRef.value) {
        
        // Tentar múltiplas abordagens
        if (formValidation.filtersRef.value.setFilters) {
          formValidation.filtersRef.value.setFilters(currentData.filters);
        } else if (formValidation.filtersRef.value.initializeFilters) {
          formValidation.filtersRef.value.initializeFilters(currentData.filters);
        } else {
          formValidation.filtersRef.value.filters = [...currentData.filters];
        }
        
        // Forçar atualização se disponível
        if (formValidation.filtersRef.value.forceUpdate) {
          formValidation.filtersRef.value.forceUpdate();
        }
        

      } else {
        console.error('❌ [tab-watch] Componente Filters não disponível');
      }
      
      // Aguardar antes de desmarcar a flag
      await new Promise(resolve => setTimeout(resolve, 200));
      isRestoringFilters.value = false;
      
    } 
  }
  
  if (newTab === 'table') {
    await nextTick();
    const currentData = store.getters['campaigns/getCampaignData'];
    
    if (currentData) {
      initialTableData.value = {
        selectedTable: currentData.selectedTable || '',
        selectedPhoneColumn: currentData.selectedPhoneColumn || '',
        selectedContactColumn: currentData.selectedContactColumn || '',
      };
      selectedTable.value = currentData.selectedTable || '';
      
      // Forçar validação quando voltar para a tab table
      if (oldTab && oldTab !== 'table') {
        await nextTick();
      }
    }
  }
  
  // Quando sair da tab table, preservar validação se já estava válida
  if (oldTab === 'table' && newTab !== 'table') {
    const currentValidation = tabManagement.tabValidation.value.table;
    
    // Se estava válida, manter válida mesmo saindo da tab
    if (currentValidation) {
      setTimeout(() => {
        if (tabManagement.tabValidation.value.table !== currentValidation) {
          tabManagement.updateValidation('table', currentValidation);
        }
      }, 100);
    }
  }
});

// 🔧 NOVO: Watch para quando o componente PlaceholdersDynamic ficar disponível
watch(() => formValidation.placeholdersDynamicRef.value, async (newRef) => {
  if (newRef && tabManagement.activeTab.value === 'placeholders') {
    
    const currentData = store.getters['campaigns/getCampaignData'];
    if (currentData && currentData.placeholders && Object.keys(currentData.placeholders).length > 0) {
      await nextTick();
      
      // Definir placeholders no componente
      newRef.placeholderSelections = { ...currentData.placeholders };
      
      // Atualizar estado local também
      formValidation.placeholderSelections.value = { ...currentData.placeholders };
      
      // Validar
      await nextTick();
      validatePlaceholdersTab();
    }
  }
});

// 🔧 NOVO: Watch para quando o componente Filters ficar disponível
watch(() => formValidation.filtersRef.value, async (newRef) => {
  if (newRef && tabManagement.activeTab.value === 'filters') {
    
    const currentData = store.getters['campaigns/getCampaignData'];
    if (currentData && currentData.filters && Array.isArray(currentData.filters) && currentData.filters.length > 0) {
      
      // 🔧 IMPORTANTE: Marcar que estamos restaurando
      isRestoringFilters.value = true;
      
      await nextTick();
      
      
      // Tentar múltiplas abordagens para definir os filtros
      if (newRef.setFilters) {
        newRef.setFilters(currentData.filters);
      } else if (newRef.initializeFilters) {
        newRef.initializeFilters(currentData.filters);
      } else {
        newRef.filters = [...currentData.filters];
      }
      
      // Atualizar estado local também
      formValidation.filters.value = [...currentData.filters];
      

      
      // Aguardar antes de desmarcar a flag
      await new Promise(resolve => setTimeout(resolve, 200));
      isRestoringFilters.value = false;
    }
  }
});

</script>

<template>



  <div class="campaign-container">


      <!-- Skeleton Loading State -->
    <div v-if="isInitializing || showSkeletonContent" class="skeleton-container">
      <!-- Header Skeleton -->
      <div class="skeleton-header">
        <div class="skeleton-title"></div>
        <div class="skeleton-subtitle"></div>
      </div>

      <!-- Main Layout Skeleton -->
      <div class="skeleton-layout">
        <!-- Left Content Skeleton -->
        <div class="skeleton-main-content">
          <!-- Tab Navigation Skeleton -->
          <div class="skeleton-tabs">
            <div class="skeleton-tab" v-for="i in 3" :key="i">
              <div class="skeleton-tab-icon"></div>
              <div class="skeleton-tab-text"></div>
            </div>
          </div>

          <!-- Tab Content Skeleton -->
          <div class="skeleton-tab-content">
            <!-- Form Fields Skeleton -->
            <div class="skeleton-form-group" v-for="i in 3" :key="i">
              <div class="skeleton-label"></div>
              <div class="skeleton-input"></div>
            </div>

            <!-- Buttons Skeleton -->
            <div class="skeleton-buttons">
              <div class="skeleton-button skeleton-button-secondary"></div>
              <div class="skeleton-button skeleton-button-primary"></div>
            </div>
          </div>
        </div>

        <!-- Right Sidebar Skeleton -->
        <div class="skeleton-sidebar">
          <div class="skeleton-preview-card">
            <div class="skeleton-preview-header"></div>
            <div class="skeleton-preview-content">
              <div class="skeleton-preview-line" v-for="i in 4" :key="i"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading Status -->
      <div class="skeleton-status">
        <div class="skeleton-pulse-dot"></div>
        <span class="skeleton-status-text">
          {{ campaignDataLoading ? 'Carregando dados da campanha...' : 'Inicializando configuração...' }}
        </span>
      </div>
    </div>


    <!-- Conteúdo principal -->
  <div v-else class="fade-in">
    <!-- Header -->
    <div class="page-header-container mb-4 sm:mb-6">
      <PageHeader
        v-if="isAppContext"
        class="w-full max-w-4xl"
        :header-title="appHeaderTitle"
        :header-content="appHeaderContent"
      />
      <PageHeader
        v-else
        class="w-full max-w-4xl"
        :header-title="$t('Limite de Mensagens do WhatsApp')"
        :header-content="computedProps.headerContent.value"
      />
    </div>

    <!-- Main Layout -->
    <div class="flex flex-col xl:flex-row gap-4 lg:gap-6">
      <!-- Main Content -->
      <div class="w-full xl:w-[70%] min-w-0 flex-1">
        <Tabs
          :model-value="tabManagement.activeTab.value"
          @update:model-value="eventHandlers.onTabChange"
          class="w-full"
        >
          <!-- Tab Navigation -->
          <TabsList class="tab-list">
            <TabsTrigger
              v-for="tab in tabManagement.tabs"
              :key="tab.id"
              :value="tab.id"
              :disabled="!tabManagement.canNavigateToTab(tab.id)"
              :class="[ { 'tab-disabled': !tabManagement.canNavigateToTab(tab.id) }]"
            >
              <div class="tab-content">
                <span class="tab-icon">{{ tab.icon }}</span>
                <span class="tab-label-full">{{ tab.label }}</span>
                <span class="tab-label-short">{{ tab.shortLabel }}</span>
                <span 
                  v-if="tabManagement.shouldShowCheck.value(tab.id)" 
                  class="tab-check"
                >
                  ✓
                </span>
              </div>
            </TabsTrigger>
          </TabsList>

          <!-- Tab Content -->
          <div class="mt-4">
            <!-- Table Configuration Tab -->
            <TabContentWrapper
              tab-id="table"
              :description="tabManagement.currentTabData?.description"
              :show-back-button="false"
              :show-preview-button="true"
              :preview-disabled="!selectedTable || isInitializing"
              :preview-button-label="t('WHATSAPP_TEMPLATES.PUBLIC.PREVIEW_TABLE_BUTTON')"
              :next-disabled="!tabManagement.tabValidation.value.table || isInitializing"
              next-button-label="Mapear Placeholders"
              @preview="eventHandlers.fetchTablePreview(selectedTable)"
              @next="eventHandlers.nextTab"
            >
              <template #content>
                <SelectedPublic
                  :ref="formValidation.selectedPublicRef"
                  :table-options="dataFetching.tableOptions.value"
                  :phone-column-options="dataFetching.phoneColumnOptions.value"
                  :name-column-options="dataFetching.nameColumnOptions.value"
                  :is-loading="dataFetching.isTableLoading"
                  :initial-selected-table="initialTableData.selectedTable"
                  :initial-selected-phone-column="initialTableData.selectedPhoneColumn"
                  :initial-selected-contact-column="initialTableData.selectedContactColumn"
                  @table-changed="eventHandlers.onTableChanged"
                  @preview-table="eventHandlers.fetchTablePreview"
                />
              </template>
            </TabContentWrapper>

            <!-- Placeholders Tab -->
            <TabContentWrapper
              tab-id="placeholders"
              :description="tabManagement.currentTabData?.description"
              back-button-label="Configurar Público"
              :next-disabled="!tabManagement.tabValidation.value.placeholders"
              next-button-label="Aplicar Filtros"
              @back="tabManagement.previousTab"
              @next="eventHandlers.nextTab"
            >
              <template #content>
                <PlaceholdersDynamic
                  :ref="formValidation.placeholdersDynamicRef"
                  :column-options="dataFetching.allColumnOptions.value"
                  :selected-template="isAppContext ? selectedTemplateCompat : computedProps.selectedTemplate.value"
                  :initial-placeholders="formValidation.placeholderSelections || {}"
                  :key="placeholderComponentKey"
                  @change="onPlaceholdersChanged"
                />
              </template>
            </TabContentWrapper>

            <!-- Filters Tab -->
            <TabContentWrapper
              tab-id="filters"
              :description="tabManagement.currentTabData?.description"
              back-button-label="Mapear Placeholders"
              :is-last-tab="true"
              :show-preview-button="true"
              :preview-disabled="!selectedTable"
              preview-button-label="Pré-visualizar Dados"
              :next-disabled="!tabManagement.canSubmit.value"
              :submit-button-label="t('CAMPAIGN.NEXT')"
              @back="tabManagement.previousTab"
              @preview="eventHandlers.fetchTablePreview(selectedTable)"
              @submit="eventHandlers.handleSubmit"
            >
              <template #content>
                <Filters
                  :ref="formValidation.filtersRef"
                  :filter-types="filterTypes"
                  :selected-table="selectedTable"
                  :initial-filters="computedProps.filtersInitialData"
                  :key="filtersComponentKey"
                  @clear-filters="formValidation.onClearFilters"
                  @preview-table="eventHandlers.fetchTablePreview"
                  @change="onFiltersChangeWrapper"
                />
              </template>
            </TabContentWrapper>
          </div>
        </Tabs>
      </div>

      <!-- Template Preview -->
      <div class="w-full xl:w-[30%] xl:min-w-0 xl:flex-shrink-0">
        <div class="sticky top-4">
          <TemplatePreviewPhone
            v-if="isAppContext && localTemplate"
            :content="localTemplate.content || ''"
            :interactive-config="localTemplate.interactive_config || {}"
          />
          <TemplatePreview
            v-else
            :selected-template="computedProps.selectedTemplate.value"
          />
        </div>
      </div>
    </div>

      <!-- Table Preview Modal -->
      <TablePreviewModal
        v-model:show="showModal"
        :tableData="dataFetching.tableData.value"
        :headerTitle="selectedTable || ''"
        :isLoading="dataFetching.isTableLoading.value"
        :totalCount="dataFetching.totalCount.value"
        :whatsappLimit="dataFetching.whatsappMessageLimit.value"
        :whatsapp-limit-error="!!dataFetching.whatsappLimitError.value"
        :whatsapp-limit-loading="dataFetching.isFetchingWhatsappLimit.value"
        :generatedSql="dataFetching.generatedSql.value"
        @close="eventHandlers.onModalClosed"
      />
    </div>
  </div>
</template>

<style scoped>
.campaign-container {
  @apply border border-woot-400 dark:border-woot-400 bg-n-background min-h-[100px];
  @apply h-full p-6 w-full max-w-full md:w-3/4 md:max-w-[75%] flex-shrink-0 flex-grow-0;
}

.page-header-container {
  @apply flex items-start gap-5;
}

.tab-list {
  @apply grid w-full grid-cols-3 rounded-lg;
  @apply bg-white dark:bg-slate-900;
  @apply border border-woot-400 dark:border-woot-400;
}

.tab-content {
  @apply flex items-center justify-center gap-1 sm:gap-2 min-w-0;
}

.tab-icon {
  @apply text-sm sm:text-base flex-shrink-0;
}

.tab-label-short {
  @apply sm:hidden truncate;
}

.tab-check {
  @apply text-green-500 dark:text-green-400 flex-shrink-0;
}

/* Skeleton Styles */
.skeleton-container {
  @apply w-full;
}

.skeleton-header {
  @apply mb-6 space-y-3;
}

.skeleton-title {
  @apply h-8 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700 rounded-lg;
  @apply w-1/3 animate-pulse;
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

.skeleton-subtitle {
  @apply h-5 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700 rounded;
  @apply w-2/3 animate-pulse;
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

.skeleton-layout {
  @apply flex flex-col xl:flex-row gap-4 lg:gap-6;
}

.skeleton-main-content {
  @apply w-full xl:w-[70%] space-y-4;
}

.skeleton-tabs {
  @apply grid grid-cols-3 gap-2 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg;
}

.skeleton-tab {
  @apply flex items-center justify-center gap-2 p-3 bg-white dark:bg-slate-700 rounded-md;
}

.skeleton-tab-icon {
  @apply w-4 h-4 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-600 dark:via-slate-500 dark:to-slate-600 rounded;
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

.skeleton-tab-text {
  @apply h-4 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-600 dark:via-slate-500 dark:to-slate-600 rounded;
  @apply w-16 flex-shrink-0;
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

.skeleton-tab-content {
  @apply bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6 space-y-6;
}

.skeleton-form-group {
  @apply space-y-2;
}

.skeleton-label {
  @apply h-4 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700 rounded;
  @apply w-24;
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

.skeleton-input {
  @apply h-10 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700 rounded-lg;
  @apply w-full;
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

.skeleton-buttons {
  @apply flex justify-between items-center pt-4;
}

.skeleton-button {
  @apply h-10 rounded-lg;
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

.skeleton-button-secondary {
  @apply w-24 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700;
}

.skeleton-button-primary {
  @apply w-32 bg-gradient-to-r from-green-200 via-green-300 to-green-200 dark:from-green-800 dark:via-green-700 dark:to-green-800;
}

.skeleton-sidebar {
  @apply w-full xl:w-[30%] xl:min-w-0 xl:flex-shrink-0;
}

.skeleton-preview-card {
  @apply bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 space-y-4;
}

.skeleton-preview-header {
  @apply h-6 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700 rounded;
  @apply w-3/4;
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

.skeleton-preview-content {
  @apply space-y-3;
}

.skeleton-preview-line {
  @apply h-4 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700 rounded;
  background-size: 200% 100%;
  animation: shimmer 2s infinite;
}

.skeleton-preview-line:nth-child(1) { @apply w-full; }
.skeleton-preview-line:nth-child(2) { @apply w-5/6; }
.skeleton-preview-line:nth-child(3) { @apply w-4/5; }
.skeleton-preview-line:nth-child(4) { @apply w-3/4; }

.skeleton-status {
  @apply flex items-center justify-center gap-3 mt-8 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700;
}

.skeleton-pulse-dot {
  @apply w-2 h-2 bg-green-500 rounded-full;
  animation: pulse 1.5s infinite;
}

.skeleton-status-text {
  @apply text-sm text-slate-600 dark:text-slate-400 font-medium;
}

/* Fade in animation for content */
.fade-in {
  animation: fadeIn 0.5s ease-in-out;
}

/* Keyframe animations */
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Responsive skeleton adjustments */
@media (max-width: 640px) {
  .skeleton-title {
    @apply w-1/2;
  }
  
  .skeleton-subtitle {
    @apply w-3/4;
  }
  
  .skeleton-tabs {
    @apply grid-cols-1 gap-2;
  }
  
  .skeleton-tab-text {
    @apply w-20;
  }
  
  .skeleton-buttons {
    @apply flex-col gap-3 items-stretch;
  }
  
  .skeleton-button-secondary,
  .skeleton-button-primary {
    @apply w-full;
  }
}

/* Existing styles */
:deep(.quality-rating-bubble) {
  @apply inline-block px-3 py-1 rounded-xl text-white font-medium text-sm uppercase;
}

* {
  box-sizing: border-box;
}

.min-w-0 {
  min-width: 0;
}

.break-words {
  word-wrap: break-word;
  overflow-wrap: break-word;
}

@media (max-width: 640px) {
  .page-header-container {
    @apply flex-col gap-3;
  }
}

:deep(.tabs-trigger) {
  transition: all 0.2s ease-in-out;
}

:deep(.tabs-trigger[data-state="active"]) {
  background: white;
  border: 1px solid rgb(34 197 94);
  color: rgb(21 128 61);
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1);
}

:deep(.tabs-trigger[data-state="active"]) .dark {
  background: rgb(15 23 42);
  color: rgb(74 222 128);
}

</style>